import { MUI_TAB_BORDER_PX } from '../../../themes/_base/tabBorder';

/**
 * The selected tab of a folder-style strip: rounded top corners, and inverse
 * corners where it meets the panel below, so it flares into the panel instead
 * of meeting it at a sharp edge. Each inverse corner is a quarter-circle cut
 * from a square of the panel's colour; both use the tile radius.
 *
 * Spread into a MUI Tab's `selected` styles, with the panel's surface colour.
 * The first tab sits on the panel's corner, so it flares on the right only.
 */
export const selectedTabCorners = (surface: string) => {
  const radius = 'var(--tile-radius, 0.25rem)';
  const corner = (side: 'left' | 'right') => ({
    content: '""',
    position: 'absolute',
    // Measured from inside the tab's transparent border; sit on its outer
    // edge instead, where the panel starts.
    bottom: -MUI_TAB_BORDER_PX,
    [side]: `calc(-1 * ${radius})`,
    width: radius,
    height: radius,
    pointerEvents: 'none',
    background: `radial-gradient(circle at ${
      side === 'left' ? '0 0' : '100% 0'
    }, transparent ${radius}, ${surface} ${radius})`,
  });

  return {
    position: 'relative',
    // Above the panel below: its shadow would otherwise draw a grey band
    // along the join, and the corners would read as feet standing on it.
    zIndex: 1,
    // MUI's Tab clips its children; the corners sit just outside it.
    overflow: 'visible',
    borderTopLeftRadius: radius,
    borderTopRightRadius: radius,
    '&::before': corner('left'),
    '&::after': corner('right'),
    '&:first-of-type::before': { display: 'none' },
  } as const;
};
